import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/TranslationContext";
import { api } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";
import { IntroNote } from "@/components/IntroNote";
import { GlossaryTerm } from "@/components/GlossaryTerm";
import { ScoreBadge, StatusBadge } from "@/components/ScoreBadge";
import { SeverityBadge } from "@/components/SeverityBadge";
import { RiskPlaybooks } from "@/components/RiskPlaybooks";
import { RiskCalibration } from "@/components/RiskCalibration";
import { RiskActivityTimeline } from "@/components/RiskActivityTimeline";
import { FairRiskPanel } from "@/components/FairRiskPanel";
import { CollapsibleList } from "@/components/CollapsibleList";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronRight, Plus, Link2, Unlink, GitBranch, ShieldAlert } from "lucide-react";

const inputCls =
  "w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-ek-ink outline-none transition-colors focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20";

const IT_LABELS = {
  it_project: "IT Project",
  it_programme: "IT Programme",
  it_operational: "IT Operational",
  it_security: "IT Security",
};

export default function Risks() {
  const { user } = useAuth();
  const { tx } = useI18n();
  const canWrite = hasPermission(user, "risk.write");
  const canReadSignals = hasPermission(user, "signal.read");

  const [themes, setThemes] = useState([]);
  const [risks, setRisks] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [selected, setSelected] = useState(null); // graph payload
  const [pbRefresh, setPbRefresh] = useState(0);
  const [allSignals, setAllSignals] = useState([]);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const emptyForm = { title: "", theme_id: "t_geopolitical", inherent_score: 12, residual_score: 9, status: "open", it_category: "" };
  const [form, setForm] = useState(emptyForm);

  // link controls
  const [linkSignalId, setLinkSignalId] = useState("");
  const [linkConfidence, setLinkConfidence] = useState(0.8);

  const loadRisks = useCallback(() => {
    return api.get("/risks").then(({ data }) => setRisks(data.items));
  }, []);

  useEffect(() => {
    api.get("/themes").then(({ data }) => setThemes(data)).catch(() => {});
    loadRisks().catch(() => toast.error("Failed to load risks"));
    if (canReadSignals) {
      api.get("/signals?limit=200&light=true").then(({ data }) => setAllSignals(data.items)).catch(() => {});
    }
  }, [loadRisks, canReadSignals]);

  const openRisk = (riskId) => {
    api
      .get(`/risks/${riskId}/graph`)
      .then(({ data }) => setSelected(data))
      .catch(() => toast.error("Failed to load risk graph"));
  };

  const refreshSelected = (riskId) => {
    api.get(`/risks/${riskId}/graph`).then(({ data }) => setSelected(data));
    loadRisks();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/risks", {
        ...form,
        owner_user_id: user.user_id,
        inherent_score: Number(form.inherent_score),
        residual_score: Number(form.residual_score),
      });
      toast.success("Risk created");
      setCreateOpen(false);
      setForm(emptyForm);
      loadRisks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create risk");
    }
  };

  const handleStatusChange = async (riskId, status) => {
    try {
      await api.patch(`/risks/${riskId}`, { status });
      toast.success(`Status set to ${status}`);
      refreshSelected(riskId);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleLink = async (riskId) => {
    if (!linkSignalId) return;
    try {
      await api.post(`/risks/${riskId}/link-signal`, {
        signal_id: linkSignalId,
        confidence: Number(linkConfidence),
      });
      toast.success("Signal linked");
      setLinkSignalId("");
      refreshSelected(riskId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link signal");
    }
  };

  const handleUnlink = async (riskId, signalId) => {
    try {
      await api.delete(`/risks/${riskId}/link-signal/${signalId}`);
      toast.success("Signal unlinked");
      refreshSelected(riskId);
    } catch {
      toast.error("Failed to unlink");
    }
  };

  const toggleGroup = (themeId) =>
    setOpenGroups((g) => ({ ...g, [themeId]: g[themeId] === false ? true : false }));

  const linkedIds = new Set((selected?.risk?.linked_signal_ids) || []);
  const linkable = allSignals.filter((s) => !linkedIds.has(s.signal_id));

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="risks-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="ek-eyebrow">{tx("Enterprise Risk Graph")}</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">{tx("Risk Register")}</h1>
            <p className="mt-3 text-base text-ek-ink/65">
              {risks.length} risks across the 9-theme ontology · linked to live signals.
            </p>
          </div>
          {canWrite && (
            <button onClick={() => setCreateOpen(true)} data-testid="add-risk-button" className="ek-btn">
              <Plus size={16} /> New risk
            </button>
          )}
        </div>

        <IntroNote icon={ShieldAlert} title="What you're looking at" testid="risks-intro" className="mt-6">
          Your enterprise risks, grouped by theme. Each shows an Inherent (I) and Residual (R) score on a 1–25
          scale and the live signals linked to it. Open any risk to quantify its £ exposure, link signals, and
          assign a playbook — this is the single source of truth for what could go wrong and how exposed you are.
        </IntroNote>

        {/* Theme groups */}
        <div className="mt-8 space-y-3">
          {themes.map((t) => {
            const groupRisks = risks.filter((r) => r.theme_id === t.theme_id);
            const isOpen = openGroups[t.theme_id] !== false;
            return (
              <div
                key={t.theme_id}
                data-testid={`risk-group-${t.theme_id}`}
                className="overflow-hidden rounded-md border border-black/5 bg-white"
              >
                <button
                  onClick={() => toggleGroup(t.theme_id)}
                  data-testid={`risk-group-toggle-${t.theme_id}`}
                  className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-ek-purple/[0.03]"
                >
                  <div className="flex items-center gap-3">
                    <ChevronRight
                      size={16}
                      className={`text-ek-purple transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                    <span className="font-display text-base font-semibold text-ek-ink">{tx(t.name)}</span>
                    <span className="rounded-full bg-ek-canvas px-2 py-0.5 text-xs font-medium text-ek-ink/65">
                      {groupRisks.length}
                    </span>
                  </div>
                </button>
                {isOpen && groupRisks.length > 0 && (
                  <div className="border-t border-black/5 px-2 pt-1 pb-2">
                    <CollapsibleList
                      testid={`risk-list-${t.theme_id}`}
                      initial={5}
                      className="divide-y divide-black/5"
                      items={groupRisks.map((r) => (
                      <button
                        key={r.risk_id}
                        onClick={() => openRisk(r.risk_id)}
                        data-testid="risk-row"
                        className="flex w-full items-center justify-between gap-4 px-3 py-3 text-left transition-colors hover:bg-ek-purple/[0.03]"
                      >
                        <span className="flex-1 truncate font-medium text-ek-ink">{tx(r.title)}</span>
                        <div className="flex items-center gap-2">
                          {r.auto_source === "supply_chain" && (
                            <span
                              data-testid="risk-auto-badge"
                              className="hidden rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 sm:inline"
                              title="Auto-raised from supply-chain analytics"
                            >
                              Auto
                            </span>
                          )}
                          {r.it_category && (
                            <span
                              data-testid={`risk-it-badge-${r.it_category}`}
                              className="hidden rounded-full border border-ek-purple/20 bg-ek-purple/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ek-purple sm:inline"
                            >
                              {IT_LABELS[r.it_category] || "IT"}
                            </span>
                          )}
                          {r.linked_signal_ids?.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-ek-ink/65">
                              <Link2 size={12} /> {r.linked_signal_ids.length}
                            </span>
                          )}
                          <ScoreBadge score={r.inherent_score} label="I" />
                          <ScoreBadge score={r.residual_score} label="R" />
                          <StatusBadge status={r.status} />
                        </div>
                      </button>
                      ))}
                    />
                  </div>
                )}
                {isOpen && groupRisks.length === 0 && (
                  <div className="border-t border-black/5 px-5 py-4 text-sm text-ek-ink/30">
                    No risks in this theme yet.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      {/* Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="risk-detail">
          {selected && (
            <>
              <SheetHeader>
                <p className="ek-eyebrow">{tx(selected.theme?.name)}</p>
                <SheetTitle className="font-display text-2xl text-ek-ink">
                  {tx(selected.risk.title)}
                </SheetTitle>
                <SheetDescription>Risk ID: {selected.risk.risk_id}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <ScoreBadge score={selected.risk.inherent_score} label="Inherent" />
                <ScoreBadge score={selected.risk.residual_score} label="Residual" />
                <StatusBadge status={selected.risk.status} />
                {selected.risk.it_category && (
                  <span
                    data-testid="risk-detail-it-badge"
                    className="rounded-full border border-ek-purple/20 bg-ek-purple/5 px-2.5 py-0.5 text-xs font-semibold text-ek-purple"
                  >
                    {IT_LABELS[selected.risk.it_category] || "IT"}
                  </span>
                )}
              </div>

              <FairRiskPanel key={selected.risk.risk_id} riskId={selected.risk.risk_id} />

              {canWrite && (
                <div className="mt-4">
                  <label className="mb-1 block text-xs font-medium text-ek-ink/65">Update status</label>
                  <select
                    value={selected.risk.status}
                    onChange={(e) => handleStatusChange(selected.risk.risk_id, e.target.value)}
                    data-testid="risk-status-select"
                    className={inputCls}
                  >
                    <option value="open">Open</option>
                    <option value="mitigating">Mitigating</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              )}

              <div className="mt-8">
                <h3 className="ek-eyebrow mb-3">
                  <GlossaryTerm k="signal" label={`Linked Signals (${selected.linked_signals.length})`} />
                </h3>
                {selected.linked_signals.length === 0 ? (
                  <p className="text-sm text-ek-ink/30">No signals linked yet.</p>
                ) : (
                  <CollapsibleList
                    testid="linked-signals"
                    initial={5}
                    className="space-y-2"
                    items={selected.linked_signals.map((s) => {
                      const link = selected.links.find((l) => l.signal_id === s.signal_id);
                      return (
                        <div
                          key={s.signal_id}
                          data-testid="linked-signal-row"
                          className="flex items-start justify-between gap-3 rounded-xl border border-black/5 bg-ek-canvas/40 p-3"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <SeverityBadge severity={s.severity} />
                              <span className="font-mono text-xs text-ek-ink/65">{s.source}</span>
                              {link && (
                                <span className="text-xs text-ek-purple">
                                  conf {Math.round(link.confidence * 100)}%
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-ek-ink/70">{s.summary}</p>
                          </div>
                          {canWrite && (
                            <button
                              onClick={() => handleUnlink(selected.risk.risk_id, s.signal_id)}
                              data-testid="unlink-signal-button"
                              className="mt-1 text-ek-ink/30 transition-colors hover:text-red-500"
                              title="Unlink"
                            >
                              <Unlink size={15} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  />
                )}
              </div>

              {/* Playbooks */}
              <RiskPlaybooks key={`pb-${selected.risk.risk_id}-${pbRefresh}`} riskId={selected.risk.risk_id} user={user} />

              {/* EKSELENS360 framework calibration + pre-validated CSI moves */}
              <RiskCalibration
                key={`cal-${selected.risk.risk_id}`}
                riskId={selected.risk.risk_id}
                user={user}
                onSeeded={() => setPbRefresh((n) => n + 1)}
              />

              {/* Activity timeline */}
              <RiskActivityTimeline key={`act-${selected.risk.risk_id}`} riskId={selected.risk.risk_id} />

              {/* Link a signal */}
              {canWrite && (
                <div className="mt-6 rounded-xl border border-ek-purple/15 bg-ek-purple/5 p-4">
                  <h3 className="ek-eyebrow mb-3 flex items-center gap-1">
                    <GitBranch size={12} /> Link a signal
                  </h3>
                  {canReadSignals ? (
                    <div className="space-y-3">
                      <select
                        value={linkSignalId}
                        onChange={(e) => setLinkSignalId(e.target.value)}
                        data-testid="link-signal-select"
                        className={inputCls}
                      >
                        <option value="">Select a signal…</option>
                        {linkable.map((s) => (
                          <option key={s.signal_id} value={s.signal_id}>
                            [{s.severity}] {s.source} — {s.summary.slice(0, 50)}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-ek-ink/65">Confidence</label>
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="0.1"
                          value={linkConfidence}
                          onChange={(e) => setLinkConfidence(e.target.value)}
                          data-testid="link-confidence-input"
                          className="w-24 rounded-lg border border-black/10 px-2 py-1 text-sm"
                        />
                        <button
                          onClick={() => handleLink(selected.risk.risk_id)}
                          disabled={!linkSignalId}
                          data-testid="link-signal-button"
                          className="ek-btn ml-auto !px-4 !py-2 disabled:opacity-50"
                        >
                          <Link2 size={14} /> Link
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-ek-ink/65">
                      Signal-read permission required to browse and link signals.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-risk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New Risk</DialogTitle>
            <DialogDescription>
              Register a risk against a theme with inherent and residual scores.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ek-ink">Title</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={120}
                required
                data-testid="risk-title-input"
                className={inputCls}
                placeholder="e.g. Cloud region single point of failure"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ek-ink">
                <GlossaryTerm k="Risk theme" label="Theme" />
              </label>
              <select
                value={form.theme_id}
                onChange={(e) => setForm({ ...form, theme_id: e.target.value })}
                data-testid="risk-theme-select"
                className={inputCls}
              >
                {themes.map((t) => (
                  <option key={t.theme_id} value={t.theme_id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-ek-ink">
                  <GlossaryTerm k="inherent" label="Inherent score (1–25)" />
                </label>
                <input
                  type="number"
                  min="1"
                  max="25"
                  value={form.inherent_score}
                  onChange={(e) => setForm({ ...form, inherent_score: e.target.value })}
                  data-testid="risk-inherent-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ek-ink">
                  <GlossaryTerm k="residual" label="Residual score (1–25)" />
                </label>
                <input
                  type="number"
                  min="1"
                  max="25"
                  value={form.residual_score}
                  onChange={(e) => setForm({ ...form, residual_score: e.target.value })}
                  data-testid="risk-residual-input"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ek-ink">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="risk-status-create-select"
                className={inputCls}
              >
                <option value="open">Open</option>
                <option value="mitigating">Mitigating</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ek-ink">
                IT classification <span className="font-normal text-ek-ink/65">(optional)</span>
              </label>
              <select
                value={form.it_category}
                onChange={(e) => setForm({ ...form, it_category: e.target.value })}
                data-testid="risk-it-category-select"
                className={inputCls}
              >
                <option value="">Not an IT risk</option>
                <option value="it_project">IT Project</option>
                <option value="it_programme">IT Programme</option>
                <option value="it_operational">IT Operational</option>
                <option value="it_security">IT Security</option>
              </select>
            </div>
            <DialogFooter>
              <button type="submit" data-testid="create-risk-submit" className="ek-btn w-full">
                Create risk
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
